import { reducer } from './reducer';
import Address from '../../lib/interfaces/Address';
import { OrderSubmitStatus } from '../../lib/enums/OrderSubmitStatus';
import { DataLoadingStatusTypes } from '../../lib/enums/DataLoadingStatusTypes';
import AllocatedStockPiece from '../../lib/interfaces/AllocatedStockPiece';
import { getDecryptedValueFromLocalStorage } from '../../lib/methods/localStorageBasedMethods';
import OrderLine from '../../lib/interfaces/OrderLine';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface OrderPadState {

  stockRefToAdd?: string;
  lines: OrderPadLine[];
  address?: Address;
  yourReference?: string;
  requiredDate?: Date;
  comments?: string;
  isSubmitting: boolean;
  submitProcessing?: OrderSubmitProcessingState;
  currentlyAmendingLineId: string;
  currentlyCloningLineId: string;
  priceLoading: DataLoadingStatusTypes;
  stockAvailableLoading: DataLoadingStatusTypes;
  importLines: boolean;
  aliasRefToAdd?: string
  extendedDescriptionToAdd?: string
  //currentlyConstructingOrderNo: number; //get from local storage when needed

  //for second order - rolls only that cannot be allocated in Realitex Web API allocation routine 
  currentlyConstructingUnpayableOrderNo: number; //this is needed in state for a useEffect, whereas currentlyConstructingOrderNo isn't
  unpayableOrderLines: OrderPadLine[];
  orderLineBeingAmendedAsNonPayableRemoved: OrderPadLine | undefined;
  
}

export interface OrderSubmitProcessingState {
  processingStatus: OrderSubmitStatus;
  processingKey?: string;
  resultLines: OrderPadLine[];
  resultLinesUnpayable: OrderPadLine[];
}

export interface OrderPadLine extends AddOrderLineItem {
  lineId: string;
  realitexOrderLineStatus: string;
  realitexOrderLineNumber: number;
  realitexOrderNumber: string;
  realitexRequiredDate?: Date;
}

export interface AddOrderLineItem {

  aliasRef: string;
  allocatedStockPieces: AllocatedStockPiece[];
  deallocateAt?: Date;
  discount?: number;
  dueDate?: Date;
  extendedDescription: string;
  grossValue?: number;
  lineComments: string;
  lineReference: string;
  nettValue?: number;
  packRollDescription: string;
  price?: number;
  priceUnavailable: boolean;
  qtyPackRoll?: number;
  qtyUnitCut?: number;
  quantity?: number;
  stockAvailable: boolean;
  stockDescription: string;
  stockReference: string;
  stockType: number;
  taxRatePercent: number;
  unit?: string;
  unitCutDescription: string;
  vatValue?: number;
  width?: number;

}

export interface DeliveryRequiredDateSettings {
  hasWeightLimit?: string;
  integratedDelRef?: string;
  integratedCustRef?: string;
  customerDeliveryRef?: string;
  customerRouteRef?: string;
  noOfDays?: number;
  maxWeight?: string;
  deliveryType?: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.


interface SetCurrentlyConstructingOrderNo {
  type: "SET_CURRENTLY_CONSTRUCTING_ORDER_NO";
  currentlyConstructingOrderNo: number
}

interface SetUnpayableOrderNo {
  type: "SET_CURRENTLY_CONSTRUCTING_UNPAYABLE_ORDER_NO";
  currentlyConstructingUnpayableOrderNo: number
}

interface AddLineAction {
  type: "ADD_LINE";
  line: AddOrderLineItem,
  useWebApiForStockPieceAllocation: boolean
}



interface UpdateDeallocateAtAction {
  type: "UPDATE_DEALLOCATE_AT";
  newDeallocateAtDateTime: Date,
  useWebApiForStockPieceAllocation: boolean
}



interface AddUnallocatableLineAction {
  type: "ADD_UNPAYABLE_LINE";
  unpayableLineToAdd: AddOrderLineItem,
  useWebApiForStockPieceAllocation: boolean
}

interface AmendLineAction {
  type: "AMEND_LINE";
  lineToAmendId: string;
}

interface UpdateLineViaAmendLineAction {
  type: "UPDATE_LINE_VIA_AMEND_LINE";
  // line: AddOrderLineItem;
  line: OrderPadLine;
  // realitexOrderLineNumber: number,
  // realitexOrderNumber: string
}

interface UpdateLineAction {
  type: "UPDATE_LINE";
  // line: AddOrderLineItem;
  updatedLine: OrderPadLine;
  // realitexOrderLineNumber: number,
  // realitexOrderNumber: string
}


interface UpdateUnpayableLineViaAmendLineAction {
  type: "UPDATE_UNPAYABLE_LINE_VIA_AMEND_LINE";
  updatedUnpayableLine: OrderPadLine;
  useWebApiForStockPieceAllocation: boolean;
}

interface CloneLineAction {
  type: "CLONE_LINE";
  lineToCloneId: string;
}

interface RemoveLineAction {
  type: "REMOVE_LINE";
  lineToRemoveId: string;
  deallocateAt: Date;
  useWebApiForStockPieceAllocation: boolean;
}

interface ClearOrderLineBeingAmendedAsNonPayableRemoved {
  type: "CLEAR_AMENDED_ORDER_LINE_AS_NONPAYABLE_REMOVED";
}

interface RemoveUnpayableLineAction {
  type: "REMOVE_UNPAYABLE_LINE";
  lineToRemoveId: string;
  deallocateAt: Date;
  useWebApiForStockPieceAllocation: boolean;

  orderLineBeingAmendedAsNonPayableRemoved? : OrderPadLine;
}

interface RemoveUnpayableLineViaAmendAction {
  type: "REMOVE_UNPAYABLE_LINE_VIA_AMEND";
  lineToRemoveId: string;
  useWebApiForStockPieceAllocation: boolean;
}

interface UpdateAddItemAction {
  type: "UPDATE_ADD_ITEM";
  stockRef: string;
  aliasRefToAdd: string;
  extendedDescriptionToAdd: string;
}

interface UpdateAddressAction {
  type: "UPDATE_ADDRESS";
  address: Address;
}

interface UpdateAdditionalInfoAction {
  type: "UPDATE_ADDITIONAL_INFO";
  yourReference: string;
  comments: string;
}

interface UpdateRequiredDateAction {
  type: "UPDATE_REQUIRED_DATE";
  requiredDate: Date;
}

interface UpdateSubmitStatusAction {
  type: "UPDATE_SUBMIT_STATUS";
  isSubmitting: boolean;
}

interface UpdateSubmitProcessingAction {
  type: "UPDATE_SUBMIT_PROCESSING";
  data: OrderSubmitProcessingState;
}

interface ResetOrderPadAction {
  type: "RESET_ORDER_PAD";
}
interface ResetOrderPadLinesAction {
  type: "RESET_ORDER_PAD_LINES";
}

interface ResetSubmitProcessingAction {
  type: "RESET_SUBMITPROCESSING";
}

interface UpdatePriceLoadingAction {
  type: "UPDATE_PRICE_LOADING";
  priceLoading: DataLoadingStatusTypes;
}

interface UpdateStockAvailableLoadingAction {
  type: "UPDATE_STOCK_AVAILBILITY_LOADING";
  stockAvailableLoading: DataLoadingStatusTypes;
}

interface ImportLinesAction {
  type: "IMPORT_LINES";
  active: boolean;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownOrderPadAction = AddLineAction | AddUnallocatableLineAction | AmendLineAction | UpdateLineViaAmendLineAction | CloneLineAction | RemoveLineAction | UpdateAddItemAction | UpdateAddressAction | UpdateAdditionalInfoAction | UpdateRequiredDateAction | UpdateSubmitStatusAction | UpdateSubmitProcessingAction | ResetOrderPadAction | ResetSubmitProcessingAction | UpdatePriceLoadingAction | UpdateStockAvailableLoadingAction | ImportLinesAction | SetCurrentlyConstructingOrderNo | UpdateDeallocateAtAction | SetUnpayableOrderNo | RemoveUnpayableLineAction | ClearOrderLineBeingAmendedAsNonPayableRemoved | UpdateLineAction | RemoveUnpayableLineViaAmendAction | UpdateUnpayableLineViaAmendLineAction | ResetOrderPadLinesAction;

export const initialStateAddOrderLineItem: AddOrderLineItem = {
  aliasRef: '',
  allocatedStockPieces: [],
  deallocateAt: undefined,
  discount: undefined,
  dueDate: undefined,
  extendedDescription: '',
  grossValue: undefined,
  lineComments: '',
  lineReference: '',
  nettValue: undefined,
  //orderLineNumber: 0,
  packRollDescription: '',
  //pieceNumbers: [],
  price: undefined,
  priceUnavailable: false,
  qtyPackRoll: undefined,
  qtyUnitCut: undefined,
  quantity: undefined,
  stockAvailable: false,
  stockDescription:'',
  stockType: 0,
  taxRatePercent: 0,
  unit: undefined,
  unitCutDescription: '',
  vatValue: undefined,
  width: undefined,
 stockReference: '',

}

export const initialStateAmendOrderLineItem: OrderPadLine = {
  aliasRef: '',
  allocatedStockPieces: [],
  deallocateAt: undefined,
  discount: undefined,
  dueDate: undefined,
  extendedDescription: '',
  grossValue: undefined,
  lineComments: '',
  lineReference: '',
  nettValue: undefined,
  //orderLineNumber: 0,
  packRollDescription: '',
  //pieceNumbers: [],
  price: undefined,
  priceUnavailable: false,
  qtyPackRoll: undefined,
  qtyUnitCut: undefined,
  quantity: undefined,
  stockAvailable: false,
  stockDescription:'',
  stockType: 0,
  taxRatePercent: 0,
  unit: undefined,
  unitCutDescription: '',
  vatValue: undefined,
  width: undefined,
  stockReference: '',

  lineId: '',
  realitexOrderLineStatus: '',
  realitexOrderLineNumber: 0,
  realitexOrderNumber: '',



}

const loggedInContactId = getDecryptedValueFromLocalStorage('ecommContactIdEnc')
const currentOrderPadLinesFromLocalStorage = getDecryptedValueFromLocalStorage(`order-pad-lines:${loggedInContactId}Enc`)
const unpayableOrderPadLines = getDecryptedValueFromLocalStorage(`order-pad-unpayable-lines:${loggedInContactId}Enc`)

export const unloadedOrderPadState: OrderPadState = {
  //currentlyConstructingOrderNo: 0,
  currentlyConstructingUnpayableOrderNo: 0,
  lines: currentOrderPadLinesFromLocalStorage ? JSON.parse(currentOrderPadLinesFromLocalStorage) : [],
  unpayableOrderLines: unpayableOrderPadLines ? JSON.parse(unpayableOrderPadLines) : [],
  stockRefToAdd: undefined,
  address: undefined,
  yourReference: undefined,
  requiredDate: undefined,
  comments: undefined,
  isSubmitting: false,
  submitProcessing: undefined,
  currentlyAmendingLineId: '',
  currentlyCloningLineId: '',
  priceLoading: DataLoadingStatusTypes.None,
  stockAvailableLoading: DataLoadingStatusTypes.None,
  importLines: false,
  aliasRefToAdd: undefined,
  extendedDescriptionToAdd: undefined,
  orderLineBeingAmendedAsNonPayableRemoved: undefined
};

export const resetOrderPadState: OrderPadState = {
  //currentlyConstructingOrderNo: 0,
  currentlyConstructingUnpayableOrderNo: 0,
  lines: [],
  unpayableOrderLines: [],
  stockRefToAdd: undefined,
  address: undefined,
  yourReference: undefined,
  requiredDate: undefined,
  comments: undefined,
  isSubmitting: false,
  currentlyAmendingLineId: '',
  currentlyCloningLineId: '',
  priceLoading: DataLoadingStatusTypes.None,
  stockAvailableLoading: DataLoadingStatusTypes.None,
  importLines: false,
  aliasRefToAdd: undefined,
  extendedDescriptionToAdd: undefined,
  orderLineBeingAmendedAsNonPayableRemoved: undefined
};

export { reducer };
